import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfFont,
  PdfBrush,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutMultiGen } from '../layoutMulti';
import { OptsMulti } from '../optsMulti';
import { GUtil } from '../../pdf/gutil';
import { genMulti } from '../../core/genHelper';
import {
  drawLcdTable,
  LCD_TABLE_WIDTH,
  LCD_TABLE_HEIGHT,
  LCD_TOP_HEIGHT,
  LCD_MARGIN,
  LCD_COLOR_PALETTES,
  LcdTableData,
} from '../../pdf/board/lcdTable';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';

import { IItemCount } from '@educraft/base';

const CHECK_SIZE = PdfUnit.FromMillimeters(7);
const CHECK_MARGIN = PdfUnit.FromMillimeters(2);

abstract class LcdTableMultiBase implements ILayoutMultiGen, IItemCount {
  constructor(protected readonly itemsPerTable: number) {}

  countDefault(): number {
    return 1;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const totalTables = this.countDefault() * 2; // Generator generates 2 tables per count
    const rnd = opts.data.random;
    const gen = genMulti(opts.data, opts.generator, this.itemsPerTable * totalTables, true, false);

    for (let i = 0; i < totalTables; i += 2) {
      const page0 = pdf.addPage(true); // front
      const page1 = pdf.addPage(true); // back

      drawLcdTable(page1, rnd, (data: LcdTableData) => {
        this.drawQuestions(
          page0,
          page1,
          gen.slice(i * this.itemsPerTable, (i + 1) * this.itemsPerTable),
          data.rect1,
          data.rect2
        );
        this.drawQuestions(
          page0,
          page1,
          gen.slice((i + 1) * this.itemsPerTable, (i + 2) * this.itemsPerTable),
          data.rect2,
          data.rect1
        );
      });
    }
  }

  private drawQuestions(
    pageFront: PageGen,
    pageBack: PageGen,
    array: ReturnType<typeof genMulti>,
    rect1: PdfRect,
    rect2: PdfRect
  ): void {
    const allTexts = array.map((i) => i.texts);
    const font = pageFront.adjustFontMulti(allTexts, GUtil.baseFont, rect1.size);

    for (let i = 0; i < array.length; i++) {
      const q = array[i];

      const cellH = rect1.height / array.length;
      const r1 = new PdfRect(rect1.x, rect1.y + i * cellH, rect1.width, cellH);
      const r2 = new PdfRect(rect2.x, rect2.y + i * cellH, rect2.width, cellH);

      const r1q = new PdfRect(r1.x, r1.y, r1.width, (r1.height * 2) / 3);
      const r1a = new PdfRect(r1.x, r1.y + (r1.height * 2) / 3, r1.width, r1.height / 3);
      const r2q = new PdfRect(r2.x, r2.y, r2.width, (r2.height * 2) / 3);
      const r2a = new PdfRect(r2.x, r2.y + (r2.height * 2) / 3, r2.width, r2.height / 3);

      pageFront.drawStringsMulti(q.texts, font, PdfBrush.Black, null, r1q, ETextAlign.Center);
      pageBack.drawStringsMulti(q.texts, font, PdfBrush.Black, null, r2q, ETextAlign.Center);

      const sz1 = new PdfRect(r1a.x, r1a.y, r1a.width / q.answers.length, r1a.height);
      const sz2 = new PdfRect(r2a.x, r2a.y, r2a.width / q.answers.length, r2a.height);

      const font2 = pageFront.adjustFont(
        sz1.width - CHECK_SIZE - 2 * CHECK_MARGIN,
        q.answers,
        GUtil.baseFont
      );

      for (let j = 0; j < q.answers.length; j++) {
        const rr1 = new PdfRect(sz1.x + sz1.width * j, sz1.y, sz1.width, sz1.height);
        const rr2 = new PdfRect(sz2.x + sz2.width * j, sz2.y, sz2.width, sz2.height);

        const rr1a = new PdfRect(
          rr1.x + PdfUnit.FromMillimeters(0.5),
          rr1.y,
          CHECK_SIZE,
          rr1.height
        );
        const rr2a = new PdfRect(
          rr2.x + PdfUnit.FromMillimeters(0.5),
          rr2.y,
          CHECK_SIZE,
          rr2.height
        );

        const rr1b = new PdfRect(
          rr1a.right + CHECK_MARGIN,
          rr1a.y,
          rr1.width - CHECK_SIZE - CHECK_MARGIN,
          rr1.height
        );
        const rr2b = new PdfRect(
          rr2a.right + CHECK_MARGIN,
          rr2a.y,
          rr2.width - CHECK_SIZE - CHECK_MARGIN,
          rr2.height
        );

        const center1 = new PdfRect(
          rr1a.x + rr1a.width / 2 - CHECK_SIZE / 2,
          rr1a.y + rr1a.height / 2 - CHECK_SIZE / 2,
          CHECK_SIZE,
          CHECK_SIZE
        );
        const center2 = new PdfRect(
          rr2a.x + rr2a.width / 2 - CHECK_SIZE / 2,
          rr2a.y + rr2a.height / 2 - CHECK_SIZE / 2,
          CHECK_SIZE,
          CHECK_SIZE
        );

        pageFront.drawEllipse(GUtil.basePen, null, center1);

        if (j === q.answerIndex) {
          pageBack.drawEllipse(null, PdfBrush.Black, center2);
        } else {
          pageBack.drawEllipse(GUtil.basePen, null, center2);
        }

        pageFront.drawString(
          q.answers[j],
          font2,
          PdfBrush.Black,
          null,
          rr1b,
          ETextAlign.CenterLeft
        );
        pageBack.drawString(q.answers[j], font2, PdfBrush.Black, null, rr2b, ETextAlign.CenterLeft);
      }

      pageFront.drawRectangle(GUtil.basePen, null, r1);
      pageBack.drawRectangle(GUtil.basePen, null, r2);
    }
  }
}

export class LCD_Tabulka__3 extends LcdTableMultiBase {
  constructor() {
    super(3);
  }
}

export class LCD_Tabulka__5 extends LcdTableMultiBase {
  constructor() {
    super(5);
  }
}
